/**
 * Routing Engine Service
 * 
 * Two paths for Boeing WDM SVG generation:
 * 1. Documents Function URL (primary) - Has S3 caching, 120s timeout
 * 2. C Routing Engine Function URL (fallback) - Direct C engine, no caching
 */

import { logger } from '../utils/logger';

// Documents handler Function URL - has S3 caching built-in
const DOCUMENTS_FUNCTION_URL = import.meta.env?.VITE_DOCUMENTS_FUNCTION_URL 
  ? String(import.meta.env.VITE_DOCUMENTS_FUNCTION_URL).trim() 
  : '';

// C Routing Engine Function URL - direct access, no caching
const ROUTING_ENGINE_FUNCTION_URL = import.meta.env?.VITE_ROUTING_ENGINE_FUNCTION_URL 
  ? String(import.meta.env.VITE_ROUTING_ENGINE_FUNCTION_URL).trim() 
  : '';

// Log configuration status on module load (helps debug deployment issues)
logger.debug('[RoutingEngine] Module loaded');
logger.debug('[RoutingEngine] Documents Function URL (with S3 cache):', !!DOCUMENTS_FUNCTION_URL);
logger.debug('[RoutingEngine] C Engine Function URL (fallback):', !!ROUTING_ENGINE_FUNCTION_URL);
if (DOCUMENTS_FUNCTION_URL) {
  logger.debug('[RoutingEngine] Primary URL:', DOCUMENTS_FUNCTION_URL.substring(0, 50) + '...');
}

export interface RoutingDevice {
  id: string;
  type: number;
  x: number;
  y: number;
  pins: Array<{
    id: string;
    x: number;  // Pin offset from device center
    y: number;
  }>;
}

export interface RoutingConnection {
  wire_id: string;
  from_device: string;
  from_pin: string;
  to_device: string;
  to_pin: string;
  priority?: number;
}

export interface RoutingRequest {
  action: 'svg' | 'route' | 'health';
  iterations?: number;
  width_mm: number;
  height_mm: number;
  devices: RoutingDevice[];
  connections: RoutingConnection[];
}

export interface RoutingMetrics {
  overlaps: number;
  crossings: number;
  total_length_mm: number;
  total_bends: number;
  wire_count: number;
  device_count: number;
  iterations_run: number;
  initial_crossings?: number;
  initial_bends?: number;
  improvements_found?: number;
  convergence_at?: number;
  compute_time_ms: number;
}

export interface RoutingSVGResponse {
  status: 'success' | 'error';
  svg?: string;
  metrics?: RoutingMetrics;
  error?: string;
}

/**
 * Export Boeing WDM SVG via Documents Function URL (with S3 caching)
 * 
 * Primary path: Uses the documents handler which has built-in S3 caching.
 * The documents handler checks S3 for cached SVG before regenerating.
 * 
 * @param projectId - The project ID
 * @param diagramId - The diagram ID  
 * @param token - Auth token
 * @returns Promise with SVG URL from S3
 */
export async function exportBoeingWDMSvg(
  projectId: string,
  diagramId: string,
  token: string
): Promise<{ url: string; cached: boolean; metrics?: RoutingMetrics }> {
  if (!DOCUMENTS_FUNCTION_URL) {
    throw new Error('Documents Function URL not configured. Set VITE_DOCUMENTS_FUNCTION_URL.');
  }

  const exportPath = `/projects/${projectId}/diagrams/${diagramId}/export`;
  const url = `${DOCUMENTS_FUNCTION_URL.replace(/\/$/, '')}${exportPath}`;
  
  logger.debug('[RoutingEngine] Calling Documents Function URL (with S3 cache)');
  logger.debug('[RoutingEngine] URL:', url.substring(0, 60) + '...');

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ format: 'svg' }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    logger.error('[RoutingEngine] Documents Function URL error:', response.status, errorText);
    throw new Error(`SVG export error: ${response.status} ${errorText}`);
  }

  const result = await response.json();
  
  if (result.data?.url) {
    logger.debug('[RoutingEngine] SVG export successful:', {
      cached: result.data.cached,
      url: result.data.url.substring(0, 50) + '...',
    });
    return {
      url: result.data.url,
      cached: result.data.cached || false,
      metrics: result.data.metrics,
    };
  }

  throw new Error(result.error || 'No SVG URL returned from export');
}

/**
 * Check if Documents Function URL is configured (primary path with S3 caching)
 */
export function isDocumentsFunctionConfigured(): boolean {
  return !!DOCUMENTS_FUNCTION_URL;
}

/**
 * Call the C routing engine directly via Function URL (no S3 caching)
 * 
 * Fallback path: Bypasses API Gateway's 29-second timeout limit,
 * allowing Monte Carlo optimization to run for up to 120 seconds.
 * Does NOT cache results in S3.
 */
export async function generateBoeingWDMSvg(request: RoutingRequest): Promise<RoutingSVGResponse> {
  if (!ROUTING_ENGINE_FUNCTION_URL) {
    throw new Error('Routing engine Function URL not configured. Set VITE_ROUTING_ENGINE_FUNCTION_URL.');
  }

  logger.debug('[RoutingEngine] Calling C Engine Function URL (no cache)');
  logger.debug('[RoutingEngine] Request:', {
    devices: request.devices.length,
    connections: request.connections.length,
    iterations: request.iterations,
  });

  const response = await fetch(ROUTING_ENGINE_FUNCTION_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    logger.error('[RoutingEngine] C Engine error:', response.status, errorText);
    throw new Error(`Routing engine error: ${response.status} ${errorText}`);
  }

  const result = await response.json();
  
  if (result.status === 'error') {
    logger.error('[RoutingEngine] Routing failed:', result.error);
    throw new Error(result.error || 'Routing failed');
  }

  logger.debug('[RoutingEngine] SVG generated (uncached):', {
    svgLength: result.svg?.length,
    metrics: result.metrics,
  });

  return result;
}

/**
 * Check if the routing engine Function URL is configured
 */
export function isRoutingEngineConfigured(): boolean {
  return !!ROUTING_ENGINE_FUNCTION_URL;
}

/**
 * Get the routing engine Function URL (for debugging)
 */
export function getRoutingEngineUrl(): string {
  return ROUTING_ENGINE_FUNCTION_URL;
}
